/**
 * HotspotHeroBar — W4.7
 *
 * Hero-Leiste: Avatar + Name + Reward-Scale-Badge + Online-% + Status-Dot.
 */
import { useTranslation } from 'react-i18next'
import type { HotspotDetail } from './useHotspotDetail'
import { deviceTypeLabel } from './deviceType'
import { RankCrown } from './RankCrown'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'

// --- Hilfsfunktionen ---

function initials(name: string | null | undefined): string {
  if (!name) return '·'
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || '·'
  )
}

function toneClass(id: number | string | null | undefined): string {
  const s = id == null ? '' : String(id)
  const sum = s.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return `tone-${(sum % 8) + 1}`
}

// --- Status-Dot ---

type StatusDotProps = { status: HotspotDetail['status'] }

function StatusDot({ status }: StatusDotProps) {
  const { t } = useTranslation('hotspots')
  const colors: Record<HotspotDetail['status'], string> = {
    online: 'var(--success)',
    warn: 'var(--warning)',
    offline: 'var(--danger)',
  }
  const labels: Record<HotspotDetail['status'], string> = {
    online: t('heroBar.statusOnline'),
    warn: t('heroBar.statusWarn'),
    offline: t('heroBar.statusOffline'),
  }
  return (
    <span
      className="status-dot"
      style={{ background: colors[status] }}
      aria-label={`Status: ${labels[status]}`}
      title={labels[status]}
    />
  )
}

// --- Props ---

type Props = {
  hotspot: HotspotDetail
  /** Optional network rank to display in badge */
  rank?: number | null
  /**
   * Zusätzliche Status-Chips (Story 1.6: CDR-Verifikation + Ban-Status),
   * gerendert in der Badge-Zeile des Heros.
   */
  chips?: React.ReactNode
}

export function HotspotHeroBar({ hotspot, rank, chips }: Props) {
  const { t } = useTranslation('hotspots')
  const displayName = hotspotDisplayName(hotspot.name, hotspot.address, t('detail.unknownName'))
  return (
    <div className="htdt-hero">
      <div className="identity hero-identity">
        {/* Avatar */}
        <div
          className={`identity-img identity-img-lg ${toneClass(hotspot.id)}`}
          aria-hidden="true"
        >
          <span>{initials(displayName)}</span>
        </div>

        {/* Meta */}
        <div className="identity-meta">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h1 className="identity-title" style={{ fontSize: 22 }}>
              {displayName}
            </h1>
            <StatusDot status={hotspot.status} />
          </div>

          <div className="identity-sub" style={{ marginTop: 4 }}>
            {hotspot.city_name && <span>{hotspot.city_name}</span>}
            {hotspot.country && <span className="muted">{hotspot.country}</span>}
            {hotspot.device_type && (
              <span className="pill" style={{ fontSize: 11, padding: '1px 7px' }}>
                {deviceTypeLabel(hotspot.device_type, t)}
              </span>
            )}
          </div>

          {/* Primaere Pill-Zeile — SPEC hotspot-detail.html: genau 3 schlanke
              Pills (Online/Warn/Offline-Status, CDR, Ban). Reward-Scale,
              DenyList und Rang sind reale Zusatzdaten, gehoeren laut Design
              aber NICHT in diese Zeile (Pill-Gate: max. 3 Chips) — sie leben
              in der schmaleren Sekundaerzeile darunter. */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}
          >
            <span
              className={`pill ${hotspot.status === 'online' ? 'pill-success' : hotspot.status === 'warn' ? 'pill-warning' : 'pill-danger'}`}
            >
              {hotspot.status === 'online'
                ? t('heroBar.statusOnline')
                : hotspot.status === 'warn'
                  ? t('heroBar.statusWarn')
                  : t('heroBar.statusOffline')}
            </span>

            {/* Status-Chips (Story 1.6: CDR + Ban) */}
            {chips}
          </div>

          {/* Sekundaerzeile — Reward-Scale, DenyList, Netzwerk-Rang. Reale,
              getestete Zusatzinfos (S10-12 / Task #17), optisch aber bewusst
              unterhalb der Pill-Gate-Zeile und kleiner gehalten. */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}
          >
            {/* Reward-Scale */}
            {hotspot.reward_scale_pct != null && (
              <span
                className={`pill ${hotspot.reward_scale_pct >= 80 ? 'pill-success' : hotspot.reward_scale_pct >= 40 ? 'pill-warning' : 'pill-danger'}`}
                title={t('heroBar.rewardScaleTitle')}
                style={{ fontSize: 11, padding: '1px 7px' }}
              >
                RS {hotspot.reward_scale_pct.toFixed(0)}%
              </span>
            )}

            {/* S10-12: DenyList-Badge in beiden Zustaenden. Quelle ist
                denylist_status (Spec), mit Fallback auf das Alias-Feld
                denylist (gleicher Boolean) fuer aeltere BE-Antworten. Nur
                rendern, wenn tatsaechlich ein boolean vorliegt — undefined
                zeigt kein Badge (statt faelschlich "Nicht auf DenyList"). */}
            {(() => {
              const denylisted = hotspot.denylist_status ?? hotspot.denylist
              if (denylisted == null) return null
              return denylisted ? (
                <span className="pill pill-danger" title={t('heroBar.denylistTitle')} style={{ fontSize: 11, padding: '1px 7px' }}>
                  {t('heroBar.denylistLabel')}
                </span>
              ) : (
                <span className="pill pill-success" title={t('heroBar.denylistOkTitle')} style={{ fontSize: 11, padding: '1px 7px' }}>
                  {t('heroBar.denylistOkLabel')}
                </span>
              )
            })()}

            {/* Rank Badge — Krone farbcodiert nach Perzentil (v1 transmit_color).
                rank kann null sein (Rank-Cron noch nicht gelaufen) → "N/A" + grau. */}
            <span className="rank-badge" title={t('heroBar.rankTitle')}>
              <RankCrown
                rank={rank ?? hotspot.rank ?? null}
                percentile={hotspot.rank_percentile ?? hotspot.rank_percentage ?? null}
                total={hotspot.rank_total ?? null}
                data-testid="hero-rank"
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
